import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BusinessHeader } from "../components/BusinessHeader";
import { ServiceList } from "../components/ServiceList";
import { StaffPicker } from "../components/StaffPicker";
import { DatePicker } from "../components/DatePicker";
import { SlotGrid } from "../components/SlotGrid";
import { CustomerForm } from "../components/CustomerForm";
import { ConfirmSummary } from "../components/ConfirmSummary";
import {
  fetchSlots,
  createBooking,
  ApiError,
  type BusinessInfo,
  type ServiceInfo,
  type MeetingMode,
  type Slot,
} from "../lib/api";
import type { CustomerFormData } from "../components/CustomerForm";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = "services" | "schedule" | "details" | "confirm";

interface Props {
  slug: string;
  business: BusinessInfo;
  services: ServiceInfo[];
  staff: Array<{ id: string; name: string }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(cents: number, currency: string): string {
  if (cents === 0) return "Free";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ClassicFlow({ slug, business, services, staff }: Props) {
  const navigate = useNavigate();

  // ── Step state ──
  const [step, setStep] = useState<Step>("services");

  // ── Step 1: Services ──
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);

  // ── Step 2: Schedule ──
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(today());
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  // ── Step 3: Details ──
  const [customer, setCustomer] = useState<CustomerFormData | null>(null);

  // ── Step 4: Confirm ──
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // ── Find-next-available state ──
  const [findNextLoading, setFindNextLoading] = useState(false);
  const [findNextError, setFindNextError] = useState<string | null>(null);

  // ── Derived values ──
  const selectedServices = services.filter((s) =>
    selectedServiceIds.includes(s.id),
  );
  const totalDuration = selectedServices.reduce(
    (sum, s) => sum + s.duration_min,
    0,
  );
  const totalPrice = selectedServices.reduce(
    (sum, s) => sum + (s.price_cents ?? 0),
    0,
  );
  const currency = selectedServices[0]?.currency ?? "USD";
  const meetingMode: MeetingMode =
    selectedServices[0]?.meeting_mode ?? "in_person";

  const selectedStaffMember =
    staff.find((s) => s.id === selectedStaffId) ?? null;

  // ── Fetch slots when date / services / staff change (on schedule step) ──
  useEffect(() => {
    if (step !== "schedule") return;
    if (selectedServiceIds.length === 0) return;
    if (!selectedDate) return;

    setSlotsLoading(true);
    setSlotsError(null);
    setSlots([]);

    fetchSlots(
      slug,
      selectedDate,
      selectedServiceIds,
      selectedStaffId ?? undefined,
    )
      .then((data) => setSlots(data))
      .catch(() => setSlotsError("Failed to load available slots."))
      .finally(() => setSlotsLoading(false));
  }, [step, slug, selectedDate, selectedServiceIds, selectedStaffId]);

  // ── Handlers ──

  function toggleService(id: string) {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function handleSelectSlot(slot: Slot) {
    setSelectedSlot(slot);
  }

  function handleDateSelect(date: string) {
    setSelectedDate(date);
    setSelectedSlot(null);
    setFindNextError(null);
  }

  function handleStaffSelect(staffId: string | null) {
    setSelectedStaffId(staffId);
    setSelectedSlot(null);
  }

  async function findNextAvailable() {
    setFindNextLoading(true);
    setFindNextError(null);

    const maxDays = business.advance_booking_days ?? 30;
    const start = new Date(selectedDate + "T00:00:00");

    for (let i = 1; i <= maxDays; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${day}`;

      try {
        const nextSlots = await fetchSlots(
          slug,
          dateStr,
          selectedServiceIds,
          selectedStaffId ?? undefined,
        );
        if (nextSlots.some((s) => s.available)) {
          setSelectedDate(dateStr);
          setSelectedSlot(null);
          setFindNextLoading(false);
          return;
        }
      } catch {
        // skip errors and keep searching
      }
    }

    setFindNextError(`No availability in the next ${maxDays} days.`);
    setFindNextLoading(false);
  }

  function handleCustomerSubmit(data: CustomerFormData) {
    setCustomer(data);
    setStep("confirm");
  }

  async function handleConfirm() {
    if (!selectedSlot || !customer) return;

    setConfirmLoading(true);
    setConfirmError(null);

    try {
      const result = await createBooking(slug, {
        service_ids: selectedServiceIds,
        staff_id: selectedStaffId ?? undefined,
        date: selectedDate,
        start_time: selectedSlot.start,
        meeting_mode: meetingMode,
        customer: {
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          notes: customer.notes || undefined,
        },
      });

      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }

      const serviceNames = selectedServices.map((s) => s.name).join(", ");
      const params = new URLSearchParams({
        token: result.cancel_token,
        id: result.appointment_id,
        date: selectedDate,
        start_time: selectedSlot.start,
        service: serviceNames,
        staff: selectedStaffMember?.name ?? "",
        meeting_mode: meetingMode,
      });
      navigate(`/${slug}/success?${params.toString()}`);
    } catch (err: unknown) {
      const isSlotUnavailable =
        (err instanceof ApiError && err.code === "SLOT_UNAVAILABLE") ||
        (err instanceof Error && err.message === "SLOT_UNAVAILABLE");

      if (isSlotUnavailable) {
        setConfirmError(
          "That time slot was just taken. Please choose another time.",
        );
        setStep("schedule");
        setSelectedSlot(null);
        // slots re-fetch automatically via useEffect dependency on step + selectedDate
      } else {
        setConfirmError(
          "Something went wrong. Please try again.",
        );
      }
    } finally {
      setConfirmLoading(false);
    }
  }

  // ── Render ──

  return (
    <div className="max-w-lg mx-auto">
      {/* Always-visible header */}
      <BusinessHeader
        business={business}
        meetingModes={[...new Set(services.map((s) => s.meeting_mode))]}
      />

      {/* Step content */}
      <div className="px-5 pb-8">
        {/* ── Step 1: Services ── */}
        {step === "services" && (
          <div className="mt-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Choose your service{services.length !== 1 ? "s" : ""}
            </h2>

            <ServiceList
              services={services}
              selected={selectedServiceIds}
              onToggle={toggleService}
            />

            {selectedServiceIds.length > 0 && (
              <button
                type="button"
                onClick={() => setStep("schedule")}
                className="w-full py-3 px-4 rounded-[var(--radius)] text-white font-medium text-sm hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "var(--accent)" }}
              >
                Continue &rarr;
              </button>
            )}
          </div>
        )}

        {/* ── Step 2: Schedule ── */}
        {step === "schedule" && (
          <div className="mt-6 space-y-5">
            <button
              type="button"
              onClick={() => setStep("services")}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1"
            >
              &larr; Back to services
            </button>

            {/* Selected services summary */}
            <div className="bg-gray-50 rounded-[var(--radius)] p-4 space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {selectedServices.map((s) => s.name).join(", ")}
              </p>
              <p className="text-xs text-gray-500">
                {totalDuration} min
                {totalPrice > 0
                  ? ` · ${formatPrice(totalPrice, currency)}`
                  : ""}
              </p>
            </div>

            {/* Staff picker */}
            {business.show_staff_names && staff.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Choose staff
                </h3>
                <StaffPicker
                  staff={staff.map((s) => ({
                    id: s.id,
                    name: s.name,
                    avatar_url: null,
                    is_active: true,
                  }))}
                  selected={selectedStaffId}
                  onSelect={handleStaffSelect}
                />
              </div>
            )}

            {/* Date picker */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Choose a date
              </h3>
              <DatePicker
                selected={selectedDate}
                onSelect={handleDateSelect}
                days={business.advance_booking_days}
              />
            </div>

            {/* Slot grid */}
            {selectedDate && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Choose a time
                </h3>
                <SlotGrid
                  slots={slots}
                  selected={selectedSlot?.start ?? null}
                  onSelect={handleSelectSlot}
                  loading={slotsLoading}
                  error={slotsError}
                  onFindNextAvailable={findNextAvailable}
                  findNextLoading={findNextLoading}
                  findNextError={findNextError}
                />
              </div>
            )}

            {/* Error from a failed booking (slot race) */}
            {confirmError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-[var(--radius)] text-red-700 text-sm">
                {confirmError}
              </div>
            )}

            {selectedSlot && (
              <button
                type="button"
                onClick={() => {
                  setConfirmError(null);
                  setStep("details");
                }}
                className="w-full py-3 px-4 rounded-[var(--radius)] text-white font-medium text-sm hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "var(--accent)" }}
              >
                Continue &rarr;
              </button>
            )}
          </div>
        )}

        {/* ── Step 3: Details ── */}
        {step === "details" && (
          <div className="mt-6 space-y-4">
            <button
              type="button"
              onClick={() => setStep("schedule")}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1"
            >
              &larr; Back
            </button>

            <h2 className="text-lg font-semibold text-gray-900">
              Your details
            </h2>

            <CustomerForm
              onSubmit={handleCustomerSubmit}
              onBack={() => setStep("schedule")}
            />
          </div>
        )}

        {/* ── Step 4: Confirm ── */}
        {step === "confirm" && selectedSlot && customer && (
          <div className="mt-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Confirm booking
            </h2>

            <ConfirmSummary
              serviceNames={selectedServices.map((s) => s.name)}
              staffName={selectedStaffMember?.name ?? null}
              date={selectedDate}
              time={selectedSlot.start}
              durationMin={totalDuration}
              priceCents={totalPrice > 0 ? totalPrice : null}
              currency={currency}
              meetingMode={meetingMode}
              onConfirm={handleConfirm}
              onBack={() => setStep("details")}
              loading={confirmLoading}
              error={confirmError}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="text-center py-4 text-xs text-gray-400">
        Powered by{" "}
        <a
          href="https://zedule.app"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-600 transition-colors"
        >
          Zedule
        </a>
      </footer>
    </div>
  );
}
